import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import classNames from 'classnames';
import { Action } from 'redux';

import lang from '../../lib/language';
import { TradingPlan } from '../../lib/models/crm/types';
import { RootState } from '../../lib/store';
import { getAutoPass, searchPlanDetails, setAutoPassEnabled } from '../../lib/store/crm';
import { getCurrentAccountTradingPlan, hasAccountOnlyDemoPlan } from '../../lib/store/crm/helpers';
import { ALL_TRANSACTIONS_PAGE_SIZE, FIRST_PAGE_NUMBER } from '../../lib/store/payment/constants';
import { isAutoPassPurchasePaymentCallback, isPaymentCallback } from '../../lib/util/url-helpers';

import WarningIcon from '../../assets/img/icon-exclamation-mark-warning.svg';
import LockIcon from '../../assets/img/lock.png';
import { GoBackButton, Grid } from '../../components';
import AwaitingPaymentNotification from '../../components/AwaitingPaymentNotification/AwaitingPaymentNotification';
import config from '../../config';
import { useElementVisibilityById, useIsMobile, useSolanaPendingPlanPayment } from '../../hooks';
import Button from '../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../util/routes';

import AutoPassModal from './AutoPassModal';
import { PlanStatusEnum } from './PlanDetails.enum';

import styles from './PlanDetails.module.scss';

const sortPlansByLinkedList = (plans: TradingPlan[]): TradingPlan[] => {
  if (plans.length === 0) return [];

  // Find the first plan (the one with no previous plan)
  const firstPlan = plans.find((plan) => plan.previous_trading_plan_id === null);

  if (!firstPlan) {
    return plans;
  }

  const sorted: TradingPlan[] = [firstPlan];

  let currentPlanId = firstPlan.id;
  while (sorted.length < plans.length) {
    // Find the next plan that has current plan's id as previous_trading_plan_id
    // eslint-disable-next-line no-loop-func
    const nextPlan = plans.find((plan) => plan.previous_trading_plan_id === currentPlanId);

    if (!nextPlan) break;

    sorted.push(nextPlan);
    currentPlanId = nextPlan.id;
  }

  return sorted;
};

const PlanDetailsRow = (title: string, value: number | string) => (
  <div className={styles.row}>
    <span className={styles.label}>{title}</span>
    <span className={styles.value}>{value}</span>
  </div>
);

const PlanDetails = (props: ReduxProps) => {
  const {
    currentPlan,
    planDetails,
    getAutoPassStatus,
    isAutoPassEnabled,
    hasOnlyDemoPlan,
    isUseDemoAccount,
    isSolanaPaymentPending,
    setAutoPassStatus,
  } = props;
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const dispatch = useDispatch();

  const isAutoPassDeactivationPaid = useMemo(() => isAutoPassPurchasePaymentCallback(), []);
  const [showAutoPassModal, setShowAutoPassModal] = useState<boolean>(false);
  const [isActivatingAutoPass, setIsActivatingAutoPass] = useState<boolean>(false);
  const [isAutoPassPayment, setIsAutoPassPayment] = useState<boolean>(isAutoPassDeactivationPaid);
  const isSolanaPendingNotification = useElementVisibilityById('awaitingPaymentNotification');
  const isSolanaPendingPlanPayment = useSolanaPendingPlanPayment();
  const isDemo = isUseDemoAccount || hasOnlyDemoPlan;
  const fullRulesUrl = config.links.selectedPlanRules;
  const shouldShowChoosePlanButton = hasOnlyDemoPlan && !isSolanaPendingPlanPayment && isSolanaPaymentPending === false;

  const toggleAutoPassModal = () => {
    setShowAutoPassModal(isAutoPassPayment ? false : !showAutoPassModal);
    setIsAutoPassPayment(false);
  };

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    // Only do this if user arrived from external page
    if (!window.history.state) {
      // Push a dummy state to block back navigation
      window.history.pushState(null, '', window.location.href);

      const onPopState = (e) => {
        // When user presses back, prevent going back outside and instead redirect to dashboard
        window.history.pushState(null, '', window.location.href);
        navigate(UI_ROUTES.dashboard, { replace: true });
      };

      window.addEventListener('popstate', onPopState);

      return () => window.removeEventListener('popstate', onPopState);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isPaymentCallback()) {
      return;
    }

    const cleanUrl = `${window.location.pathname}`;
    window.history.replaceState({}, document.title, cleanUrl);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const searchPlanDetailsCallback = useCallback(
    (referenceId: string) => {
      dispatch(
        searchPlanDetails({
          page_number: FIRST_PAGE_NUMBER,
          page_size: ALL_TRANSACTIONS_PAGE_SIZE,
          order_column: 'Id',
          order_ascending: true,
          tenant_id: config.auth.tenantId,
          reference_id: referenceId,
          work_flow_depth: 3,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    if (currentPlan?.reference_id) {
      searchPlanDetailsCallback(currentPlan.reference_id);
    }
  }, [currentPlan?.reference_id, searchPlanDetailsCallback]);

  useEffect(() => {
    if (isAutoPassDeactivationPaid) {
      setAutoPassStatus(false);
      return;
    }
    getAutoPassStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sortedPlanDetails = useMemo(() => sortPlansByLinkedList([...planDetails]), [planDetails]);

  // Map the trading plans to stages for display
  const currentPlanIndex = sortedPlanDetails.findIndex(
    (p) => p.reference_id === currentPlan?.reference_id,
  );

  const onAutoPassButtonClick = () => {
    if (!isAutoPassEnabled) {
      setIsActivatingAutoPass(true);
    } else {
      setIsActivatingAutoPass(false);
    }
    toggleAutoPassModal();
  };

  const stages = sortedPlanDetails.map((plan, index) => {
    const isCurrentPlan = plan.reference_id === currentPlan?.reference_id;

    let status = PlanStatusEnum.Locked;
    if (isCurrentPlan) {
      status = PlanStatusEnum.Current;
    } else if (index < currentPlanIndex) {
      status = PlanStatusEnum.Completed;
    }

    return {
      name: plan.phase,
      status,
      buyingPower: `$${plan.daily_hard_bp?.toLocaleString() || 0}`,
      profitShare: `${plan.payout_split || 0}%`,
      profitTarget: plan.profit_target_amount
        ? `$${plan.profit_target_amount.toLocaleString()}`
        : lang.notAvailable(),
      maxDrawdown: plan.maximum_total_loss_amount
        ? `$${plan.maximum_total_loss_amount.toLocaleString()}`
        : lang.notAvailable(),
      maxDailyLoss: plan.daily_loss_limit_amount
        ? `$${plan.daily_loss_limit_amount.toLocaleString()}`
        : lang.notAvailable(),
      minTradingDays: plan.min_trading_days_target
        ? `${plan.min_trading_days_target} ${lang.commonDays()}`
        : lang.notAvailable(),
      consistencyRule: plan.consistency_coefficient
        ? `${plan.consistency_coefficient}%`
        : lang.notAvailable(),
      riskManagementRule: plan.no_gambling_rule ? lang.commonYes() : lang.commonNo(),
    };
  });

  const getDemoInfo = () => (
    <div className={classNames(styles.demoInfo, { [styles.demoInfoExtra]: hasOnlyDemoPlan })}>
      <div className={styles.titleSection}>
        <h1 className={styles.title}>
          {isDemo ? lang.demoPlanDetails() : lang.commonPlanDetails()}
        </h1>
        <p className={styles.subtitle}>
          {lang.planDetailsSubtitle()}
          <a
            className={styles.link}
            href={fullRulesUrl}
            target="_blank"
            rel="noreferrer"
          >
            {` ${lang.commonHere()}.`}
          </a>
        </p>
      </div>
      {shouldShowChoosePlanButton && (
        <Button
          className={styles.choosePlanButton}
          variant={ButtonTypesEnum.Primary}
          biggerFontSize
          id="ChoosePlanButton"
          onClick={() => navigate(UI_ROUTES.products)}
        >
          {lang.commonSelectPlan()}
        </Button>
      )}
    </div>
  );

  const isLoading = !planDetails || planDetails.length === 0;

  const renderLoading = () => (
    <div className={styles.header}>
      <div className={styles.headerContent}>
        {!isMobile && (
          <div className={styles.goBack}>
            <GoBackButton onBackBtnPress={() => navigate(UI_ROUTES.dashboard)} />
          </div>
        )}
        <div className={styles.titleSection}>
          <h1 className={styles.title}>{lang.commonPlanDetails()}</h1>
          <p className={styles.subtitle}>{lang.loadingPlanDetails()}</p>
        </div>
      </div>
    </div>
  );

  const renderHeader = () => (
    <div className={styles.header}>
      <div className={styles.headerContent}>
        {!isMobile && (
          <div className={styles.goBack}>
            <GoBackButton onBackBtnPress={() => navigate(UI_ROUTES.dashboard)} />
          </div>
        )}
        {getDemoInfo()}
      </div>
    </div>
  );

  const renderContent = () => (
    <div className={classNames({ [styles.content]: !isDemo }, { [styles.contentFlex]: isDemo })}>
      {!isDemo && (
        <>
          <AwaitingPaymentNotification />
          {!isSolanaPendingNotification && (
            <div className={styles.autoPassBanner}>
              <div className={styles.autoPassContent}>
                <div className={styles.autoPassIconAndTitle}>
                  {!isAutoPassEnabled && (
                    <img src={WarningIcon} alt="WarningIcon" className={styles.autoPassIcon} />
                  )}
                  <div className={styles.autoPassTitle}>
                    {isAutoPassEnabled ? lang.autoPassIsActivated() : lang.autoPassNotActivated()}
                  </div>
                </div>
                <div className={styles.autoPassText}>{lang.autoPassActivateDescription()}</div>
              </div>
              <button
                type="button"
                className={classNames(styles.activateButton, {
                  [styles.deactivateButton]: isAutoPassEnabled,
                })}
                onClick={onAutoPassButtonClick}
              >
                {isAutoPassEnabled ? lang.deactivateAutoPass() : lang.activateAutoPass()}
              </button>
            </div>
          )}
          <div className={styles.progressBar}>
            <div className={styles.stages}>
              {stages.map((stage, index) => {
                const nextStage = stages[index + 1];
                const isLineGreen = (stage.status === PlanStatusEnum.Completed
                    || stage.status === PlanStatusEnum.Current)
                  && nextStage
                  && (nextStage.status === PlanStatusEnum.Completed
                    || nextStage.status === PlanStatusEnum.Current);
                return (
                  <React.Fragment key={stage.name}>
                    <div className={styles.stageIndicator}>
                      <div className={`${styles.stageDot} ${styles[stage.status]}`}>
                        {stage.status === PlanStatusEnum.Completed && (
                          <span className={styles.checkmark}>✓</span>
                        )}
                        {stage.status === PlanStatusEnum.Locked && (
                          <img src={LockIcon} alt="Lock" />
                        )}
                      </div>
                    </div>
                    {index < stages.length - 1 && (
                      <div
                        className={`${styles.progressLine} ${isLineGreen ? styles.progressLineActive : ''}`}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <div className={styles.stageNames}>
              {stages.map((stage) => {
                let statusClass = styles.locked;
                if (stage.status === PlanStatusEnum.Completed) {
                  statusClass = styles.completed;
                } else if (stage.status === PlanStatusEnum.Current) {
                  statusClass = styles.current;
                }

                return (
                  <div
                    key={`name-${stage.name}`}
                    className={classNames(styles.stageName, statusClass)}
                  >
                    {stage.name}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      <div className={isDemo ? styles.demoContainer : styles.stagesContainer}>
        {stages.slice(0, isDemo ? 1 : stages.length).map((stage) => (
          <div
            key={stage.name}
            className={classNames(styles.stageCard, {
              [styles.demoStageCard]: isDemo,
            })}
          >
            {!isDemo && (
              <div className={styles.stageHeader}>
                <h3 className={styles.stageTitleCard}>{stage.name}</h3>
                {stage.status === PlanStatusEnum.Current && (
                  <span className={styles.currentBadge}>{lang.currentStage()}</span>
                )}
                {stage.status === PlanStatusEnum.Locked && (
                  <span className={styles.lockedBadge}>
                    <img src={LockIcon} alt="Lock" className={styles.lockImage} />
                    {lang.locked()}
                  </span>
                )}
              </div>
            )}

            <div className={styles.stageContent}>
              {PlanDetailsRow(lang.buyingPower(), stage.buyingPower)}
              {PlanDetailsRow(lang.profitShare(), stage.profitShare)}
              {PlanDetailsRow(lang.profitTarget(), stage.profitTarget)}
              {PlanDetailsRow(lang.maxDrawdown(), stage.maxDrawdown)}
              {PlanDetailsRow(lang.maxDailyLoss(), stage.maxDailyLoss)}
              {PlanDetailsRow(lang.minTradingDays(), stage.minTradingDays)}
              {PlanDetailsRow(lang.consistencyRule(), stage.consistencyRule)}
              {PlanDetailsRow(lang.riskManagementRule(), stage.riskManagementRule)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Grid>
      <div className={styles.container}>
        {(showAutoPassModal || isAutoPassPayment) && (
          <AutoPassModal
            toggleModal={toggleAutoPassModal}
            isAutoPassPayment={isAutoPassPayment}
            isActivatingAutoPass={isActivatingAutoPass}
          />
        )}
        {isLoading ? (
          renderLoading()
        ) : (
          <>
            {renderHeader()}
            {renderContent()}
          </>
        )}
      </div>
    </Grid>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentPlan: getCurrentAccountTradingPlan(state.crm),
  planDetails: state.crm.planDetailsFlow,
  isAutoPassEnabled: state.crm.isAutoPassEnabled,
  hasOnlyDemoPlan: hasAccountOnlyDemoPlan(state.crm),
  isUseDemoAccount: state.crm.isUseDemoAccount,
  isSolanaPaymentPending: state.payment.isSolanaPaymentPending,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  getAutoPassStatus: () => dispatch(getAutoPass()),
  setAutoPassStatus: (isEnabled: boolean) => dispatch(setAutoPassEnabled(isEnabled)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PlanDetails);
